/**
 * LeftShield Portal - AI Advisor Page
 * Full-page chat interface for the AI Narrator with session management,
 * context-aware prompts, and response rating.
 *
 * Route: /ai-advisor | Nav: Intelligence > AI Advisor
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Button,
  Spinner,
  Input,
  Dropdown,
  Option,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  SendRegular,
  AddRegular,
  ArchiveRegular,
  ThumbLikeRegular,
  ThumbDislikeRegular,
  ChatRegular,
  SparkleRegular,
  CheckmarkCircleRegular,
  ShieldCheckmarkRegular,
  DocumentSearchRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { narratorApi } from '../services/api';
import { useDashboardOverview } from '../hooks/useDashboardOverview';
import type {
  ChatSession,
  SendMessageRequest,
  ContextType,
} from '../types';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    height: 'calc(100vh - 120px)',
    gap: 0,
  },
  sidebar: {
    width: '260px',
    minWidth: '260px',
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sidebarHeader: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  sessionList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
  },
  sessionItem: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  sessionItemActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  chatHeader: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  messageRow: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '70%',
  },
  messageRowUser: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
  },
  messageRowAssistant: {
    alignSelf: 'flex-start',
    alignItems: 'flex-start',
  },
  messageBubble: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: 'break-word' as const,
  },
  messageBubbleUser: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  messageBubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  messageTimestamp: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    marginTop: tokens.spacingVerticalXXS,
  },
  ratingRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
  inputArea: {
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-end',
  },
  suggestedPrompts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXL,
  },
  promptChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'center',
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalXXL,
  },
  structuredBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  structuredSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  structuredLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  greetingIcon: {
    fontSize: tokens.fontSizeHero900,
    color: tokens.colorBrandForeground1,
  },
  insightCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  insightHeading: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  insightHeadingSecondary: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteTealForeground2,
  },
  insightGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    width: '100%',
    maxWidth: '700px',
    marginTop: tokens.spacingVerticalL,
  },
  actionChips: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap' as const,
    marginTop: tokens.spacingVerticalS,
  },
  confidenceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    marginTop: tokens.spacingVerticalXXS,
  },
});

// Intent-aware suggested prompts with context types
const SUGGESTED_PROMPTS: { label: string; intent: string; contextType: ContextType }[] = [
  { label: 'Explain why a PR was blocked', intent: 'explain_blocked_pr', contextType: 'Run' },
  { label: 'Why is a control failing?', intent: 'why_control_failing', contextType: 'Framework' },
  { label: 'Show compliance coverage gaps', intent: 'summarize_compliance_gaps', contextType: 'Dashboard' },
  { label: 'What evidence is missing?', intent: 'show_missing_evidence', contextType: 'Framework' },
  { label: 'How has risk changed this week?', intent: 'summarize_risk_changes', contextType: 'Dashboard' },
];

const CONTEXT_TYPES: { value: ContextType; label: string }[] = [
  { value: 'General', label: 'General' },
  { value: 'Dashboard', label: 'Dashboard' },
  { value: 'Framework', label: 'Framework' },
  { value: 'Finding', label: 'Finding' },
  { value: 'Vulnerability', label: 'Vulnerability' },
  { value: 'Run', label: 'Run' },
  { value: 'Report', label: 'Report' },
];

/** Strip [Platform Context] block from user messages for display */
const stripPlatformContext = (content: string): string => {
  const idx = content.indexOf('\n\n[Platform Context]');
  return idx >= 0 ? content.substring(0, idx).trim() : content;
};

/** Attempt to parse structured response sections from assistant messages */
interface StructuredResponse {
  answer?: string;
  why?: string;
  nextActions?: string[];
  confidence?: number;
  raw?: string;
}

const parseStructuredResponse = (content: string): StructuredResponse | null => {
  // Try to detect structured sections by common headers
  const sections: Record<string, string> = {};
  let currentSection = 'answer';
  const lines = content.split('\n');
  const sectionPatterns: Record<string, RegExp> = {
    answer: /^(?:\*\*)?(?:Answer|Direct Answer|Summary)(?:\*\*)?:?\s*/i,
    why: /^(?:\*\*)?(?:Why|Explanation|Reason|Because|Context)(?:\*\*)?:?\s*/i,
    nextActions: /^(?:\*\*)?(?:Next Actions|Actions|Recommendations|Next Steps|Suggested Actions)(?:\*\*)?:?\s*/i,
    confidence: /^(?:\*\*)?(?:Confidence)(?:\*\*)?:?\s*/i,
  };

  for (const line of lines) {
    let matched = false;
    for (const [section, pattern] of Object.entries(sectionPatterns)) {
      if (pattern.test(line)) {
        currentSection = section;
        const remaining = line.replace(pattern, '').trim();
        if (remaining) {
          sections[currentSection] = (sections[currentSection] ?? '') + remaining + '\n';
        }
        matched = true;
        break;
      }
    }
    if (!matched) {
      sections[currentSection] = (sections[currentSection] ?? '') + line + '\n';
    }
  }

  // Only treat as structured if we found at least 2 distinct sections
  const foundSections = Object.keys(sections).filter(k => sections[k]?.trim());
  if (foundSections.length < 2) return null;

  const nextActionLines = sections.nextActions?.split('\n')
    .map(l => l.replace(/^[-•*\d.)\s]+/, '').trim())
    .filter(l => l.length > 0) ?? [];

  const confidenceMatch = sections.confidence?.match(/([\d.]+)/);

  return {
    answer: sections.answer?.trim(),
    why: sections.why?.trim(),
    nextActions: nextActionLines.length > 0 ? nextActionLines : undefined,
    confidence: confidenceMatch ? parseFloat(confidenceMatch[1]) : undefined,
  };
};

/** Suggested follow-up actions based on AI response content */
const getFollowUpActions = (content: string): { label: string; icon: React.ReactElement; contextType: ContextType }[] => {
  const actions: { label: string; icon: React.ReactElement; contextType: ContextType }[] = [];
  const lower = content.toLowerCase();

  if (lower.includes('compliance') || lower.includes('framework') || lower.includes('control')) {
    actions.push({ label: 'Show compliance gaps', icon: <DocumentSearchRegular />, contextType: 'Framework' });
  }
  if (lower.includes('enforcement') || lower.includes('policy') || lower.includes('blocked')) {
    actions.push({ label: 'Explain enforcement', icon: <ShieldCheckmarkRegular />, contextType: 'Dashboard' });
  }
  if (lower.includes('evidence') || lower.includes('finding') || lower.includes('remediat')) {
    actions.push({ label: 'What needs fixing?', icon: <CheckmarkCircleRegular />, contextType: 'Finding' });
  }

  return actions.slice(0, 3);
};

export const AIAdvisorPage: React.FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Tracks whether the greeting has been shown for the current thread.
  // We render the greeting only when there is no active session AND no messages —
  // the ref guarantees it never re-renders mid-thread even during transient state.
  const hasGreetedRef = useRef<boolean>(false);

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [contextType, setContextType] = useState<ContextType>('General');

  // Live dashboard data for header insights only (NOT for client-side context enrichment —
  // platform context is now assembled server-side via AdvisorContextService).
  const { data: dashboardData } = useDashboardOverview();

  // Fetch all sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery<ChatSession[]>({
    queryKey: ['narrator', 'sessions'],
    queryFn: () => narratorApi.getSessions(),
  });

  // Fetch active session details
  const { data: activeSession, isLoading: sessionLoading } = useQuery<ChatSession>({
    queryKey: ['narrator', 'session', activeSessionId],
    queryFn: () => narratorApi.getSession(activeSessionId!),
    enabled: !!activeSessionId,
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (request: SendMessageRequest) => narratorApi.sendMessage(request),
    onSuccess: (response) => {
      if (response.sessionId) setActiveSessionId(response.sessionId);
      setMessageInput('');
      queryClient.invalidateQueries({ queryKey: ['narrator', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['narrator', 'session'] });
    },
  });

  // Rate response mutation
  const rateMutation = useMutation({
    mutationFn: ({ sessionId, rating }: { sessionId: string; rating: string }) =>
      narratorApi.rateResponse(sessionId, rating),
    onSuccess: () => {
      if (activeSessionId) {
        queryClient.invalidateQueries({ queryKey: ['narrator', 'session', activeSessionId] });
      }
    },
  });

  // Archive session mutation
  const archiveMutation = useMutation({
    mutationFn: (id: string) => narratorApi.archiveSession(id),
    onSuccess: () => {
      setActiveSessionId(null);
      queryClient.invalidateQueries({ queryKey: ['narrator', 'sessions'] });
    },
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeSession?.messages]);

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    // Send the user's message verbatim — the API server now classifies intent
    // and assembles platform context internally via AdvisorContextService.
    hasGreetedRef.current = true;
    sendMessageMutation.mutate({
      sessionId: activeSessionId ?? undefined,
      message: messageInput.trim(),
      contextType,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt: { label: string; intent: string; contextType: ContextType }) => {
    setContextType(prompt.contextType);
    setMessageInput(prompt.label);
    hasGreetedRef.current = true;
    sendMessageMutation.mutate({
      sessionId: activeSessionId ?? undefined,
      message: prompt.label,
      contextType: prompt.contextType,
    });
  };

  const handleNewChat = () => {
    setActiveSessionId(null);
    setMessageInput('');
    // Reset greeting visibility for the new thread.
    hasGreetedRef.current = false;
  };

  const messages = activeSession?.messages ?? [];
  const activeSessions = sessions?.filter((s) => s.status === 'Active') ?? [];

  return (
    <div className={styles.page}>
      {/* Left Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={handleNewChat}
            style={{ width: '100%' }}
          >
            New Chat
          </Button>
        </div>
        <div className={styles.sessionList}>
          {sessionsLoading ? (
            <Spinner size="small" label="Loading sessions..." />
          ) : activeSessions.length > 0 ? (
            activeSessions.map((session) => (
              <div
                key={session.id}
                className={`${styles.sessionItem} ${
                  activeSessionId === session.id ? styles.sessionItemActive : ''
                }`}
                onClick={() => setActiveSessionId(session.id)}
              >
                <Body1 style={{ fontWeight: activeSessionId === session.id ? 600 : 400 }}>
                  {session.title || 'Untitled Chat'}
                </Body1>
                <Body2 style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase100 }}>
                  {session.lastMessageAt
                    ? new Date(session.lastMessageAt).toLocaleDateString()
                    : new Date(session.createdAt).toLocaleDateString()}
                </Body2>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Body2>No conversations yet</Body2>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={styles.mainArea}>
        {/* Chat Header */}
        {activeSessionId && activeSession && (
          <div className={styles.chatHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
              <ChatRegular style={{ fontSize: '1.25rem' }} />
              <Title2>{activeSession.title || 'AI Advisor'}</Title2>
              <Badge appearance="outline">{activeSession.contextType}</Badge>
            </div>
            <Button
              appearance="subtle"
              icon={<ArchiveRegular />}
              onClick={() => archiveMutation.mutate(activeSessionId)}
              disabled={archiveMutation.isPending}
            >
              Archive
            </Button>
          </div>
        )}

        {/* Messages Area — greeting renders ONLY on a brand-new thread (no session, no
            messages, hasGreeted flag false). Once the user sends or selects a prompt,
            hasGreetedRef flips and the greeting never reappears mid-conversation. */}
        {!activeSessionId && messages.length === 0 && !hasGreetedRef.current ? (
          <div className={styles.suggestedPrompts}>
            <SparkleRegular className={styles.greetingIcon} />
            <Title2 block align="center">AI Advisor</Title2>
            <Body1 block align="center" style={{ color: tokens.colorNeutralForeground3 }}>
              Ask questions about your security posture, compliance status, or get remediation recommendations.
            </Body1>
            <div className={styles.promptChips}>
              {SUGGESTED_PROMPTS.map((prompt) => (
                <Button
                  key={prompt.intent}
                  appearance="outline"
                  size="medium"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  disabled={sendMessageMutation.isPending}
                >
                  {prompt.label}
                </Button>
              ))}
            </div>

            {/* F094: Top Governance Insights & Recommended Remediations */}
            <div className={styles.insightGrid}>
              <div className={styles.insightCard}>
                <Body1 className={styles.insightHeading}>Top Governance Insights</Body1>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {dashboardData?.criticalFindings ? `${dashboardData.criticalFindings} critical findings need attention` : 'No critical findings detected'}
                </Body2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {dashboardData?.enforcement?.totalEvaluations ? `${dashboardData.enforcement.totalEvaluations} policy evaluations in last 30 days` : 'Policy engine awaiting first evaluation'}
                </Body2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {dashboardData?.evidenceChain?.isValid ? 'Evidence chain verified and intact' : 'Evidence chain pending verification'}
                </Body2>
              </div>
              <div className={styles.insightCard}>
                <Body1 className={styles.insightHeadingSecondary}>Recommended Actions</Body1>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {dashboardData?.enforcementRate && dashboardData.enforcementRate < 80 ? 'Review enforcement rate — currently below 80%' : 'Enforcement rate is healthy'}
                </Body2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {dashboardData?.enforcement?.blockedCount ? `${dashboardData.enforcement.blockedCount} blocked actions — investigate patterns` : 'No blocked actions to review'}
                </Body2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  Ask AI to explain any compliance gaps or suggest policy improvements
                </Body2>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.messagesArea}>
            {sessionLoading ? (
              <Spinner size="small" label="Loading conversation..." />
            ) : (
              messages
                .filter((msg) => msg.role !== 'System')
                .map((msg, idx, filteredMsgs) => {
                  const displayContent = msg.role === 'User'
                    ? stripPlatformContext(msg.content)
                    : msg.content;
                  const structured = msg.role === 'Assistant'
                    ? parseStructuredResponse(displayContent)
                    : null;
                  const isLastAssistant = msg.role === 'Assistant' && idx === filteredMsgs.length - 1;
                  const followUpActions = isLastAssistant ? getFollowUpActions(displayContent) : [];

                  return (
                  <div
                    key={idx}
                    className={`${styles.messageRow} ${
                      msg.role === 'User' ? styles.messageRowUser : styles.messageRowAssistant
                    }`}
                  >
                    <div
                      className={`${styles.messageBubble} ${
                        msg.role === 'User'
                          ? styles.messageBubbleUser
                          : styles.messageBubbleAssistant
                      }`}
                    >
                      {structured ? (
                        <div className={styles.structuredBlock}>
                          {structured.answer && (
                            <div className={styles.structuredSection}>
                              <Body1 style={{ whiteSpace: 'pre-wrap' }}>{structured.answer}</Body1>
                            </div>
                          )}
                          {structured.why && (
                            <div className={styles.structuredSection}>
                              <Caption1 className={styles.structuredLabel}>Why</Caption1>
                              <Body2 style={{ whiteSpace: 'pre-wrap', color: tokens.colorNeutralForeground2 }}>{structured.why}</Body2>
                            </div>
                          )}
                          {structured.nextActions && structured.nextActions.length > 0 && (
                            <div className={styles.structuredSection}>
                              <Caption1 className={styles.structuredLabel}>Next Actions</Caption1>
                              {structured.nextActions.map((action, i) => (
                                <Body2 key={i} style={{ color: tokens.colorNeutralForeground2 }}>
                                  {`${i + 1}. ${action}`}
                                </Body2>
                              ))}
                            </div>
                          )}
                          {structured.confidence != null && (
                            <div className={styles.confidenceBadge}>
                              <Badge appearance="outline" size="small" color="informative">
                                Confidence: {structured.confidence <= 1
                                  ? `${Math.round(structured.confidence * 100)}%`
                                  : `${Math.round(structured.confidence)}%`}
                              </Badge>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Body1 style={{ whiteSpace: 'pre-wrap' }}>{displayContent}</Body1>
                      )}
                    </div>
                    <div className={styles.messageTimestamp}>
                      {new Date(msg.createdAt).toLocaleTimeString()}
                    </div>
                    {msg.role === 'Assistant' && activeSessionId && (
                      <>
                        <div className={styles.ratingRow}>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<ThumbLikeRegular />}
                            onClick={() =>
                              rateMutation.mutate({
                                sessionId: activeSessionId,
                                rating: 'Helpful',
                              })
                            }
                            disabled={rateMutation.isPending}
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<ThumbDislikeRegular />}
                            onClick={() =>
                              rateMutation.mutate({
                                sessionId: activeSessionId,
                                rating: 'Unhelpful',
                              })
                            }
                            disabled={rateMutation.isPending}
                          />
                        </div>
                        {followUpActions.length > 0 && (
                          <div className={styles.actionChips}>
                            {followUpActions.map((action) => (
                              <Button
                                key={action.label}
                                appearance="outline"
                                size="small"
                                icon={action.icon}
                                onClick={() => {
                                  setContextType(action.contextType);
                                  setMessageInput(action.label);
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  );
                })
            )}
            {sendMessageMutation.isPending && (
              <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
                <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                  <Spinner size="tiny" label="Thinking..." />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input Area */}
        <div className={styles.inputArea}>
          <Dropdown
            value={contextType}
            onOptionSelect={(_, data) => setContextType(data.optionValue as ContextType)}
            style={{ minWidth: '130px' }}
          >
            {CONTEXT_TYPES.map((ct) => (
              <Option key={ct.value} value={ct.value}>
                {ct.label}
              </Option>
            ))}
          </Dropdown>
          <Input
            placeholder="Ask the AI Advisor..."
            value={messageInput}
            onChange={(_, data) => setMessageInput(data.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
            disabled={sendMessageMutation.isPending}
          />
          <Button
            appearance="primary"
            icon={<SendRegular />}
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || sendMessageMutation.isPending}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};
